import { type FC } from 'react';
import { useFragment, graphql, useLazyLoadQuery, usePaginationFragment } from 'react-relay';

import { triggerConstants } from 'dibs-tracking';
import { ItemTilesCarouselShimmer } from 'dibs-product-tile-carousel/exports/ItemTilesCarouselShimmer.ts';
import { getBuyerId, getPriceBookName } from 'dibs-cookie-jar';
import { flattenConnectionNodes } from 'dibs-ts-utils/exports/flattenConnectionNodes.ts';
import { useClientState } from 'dibs-react-hooks/src/useClientState.ts';
import { getUserSessionCountryCode } from 'dibs-regional-info/exports/regionalInfoHelpers.ts';

import { useHomePageContext } from '../HomePageContext.tsx';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient.ts';

import { type HpSharedFeaturedModuleCarousel_user$key } from './__generated__/HpSharedFeaturedModuleCarousel_user.graphql.ts';
import { type HpSharedFeaturedModuleCarouselQuery } from './__generated__/HpSharedFeaturedModuleCarouselQuery.graphql.ts';
import { type HpSharedFeaturedModuleCarousel_viewer$key } from './__generated__/HpSharedFeaturedModuleCarousel_viewer.graphql.ts';
import { type HpSharedFeaturedModuleCarouselPaginationQuery } from './__generated__/HpSharedFeaturedModuleCarouselPaginationQuery.graphql.ts';

const HpSharedItemTilesCarousel = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "HpSharedItemTilesCarousel" */ '../HpSharedItemTilesCarouselModule/HpSharedItemTilesCarousel.tsx'
        )
);

const ITEMS_PER_PAGE = 5;
const MAX_ITEMS_PER_VERTICAL = 10;
const CAROUSEL_ITEMS_TO_SHOW_RESP = 5;
const CAROUSEL_ITEMS_TO_SHOW_MOBILE = 1.5;

const viewerFragment = graphql`
    fragment HpSharedFeaturedModuleCarousel_viewer on Viewer
    @refetchable(queryName: "HpSharedFeaturedModuleCarouselPaginationQuery")
    @argumentDefinitions(
        count: { type: "Int", defaultValue: 5 }
        cursor: { type: "String" }
        uriRef: { type: "String!" }
        hasUserId: { type: "Boolean!" }
        userId: { type: "String!" }
        isTrade: { type: "Boolean!" }
        priceBookName: { type: "String" }
        userCountryCode: { type: "String" }
    ) {
        saleItems: itemSearchItems(
            first: $count
            after: $cursor
            uriRef: $uriRef
            priceBookName: $priceBookName
            userCountryCode: $userCountryCode
        ) @connection(key: "HpSharedFeaturedModuleCarousel_saleItems", filters: ["uriRef"]) {
            # eslint-disable-next-line relay/unused-fields
            edges {
                node {
                    ...HpSharedItemTilesCarousel_items
                        @arguments(
                            fetchFavorites: $hasUserId
                            userId: $userId
                            isTrade: $isTrade
                            priceBookName: $priceBookName
                        )
                }
            }
            totalResults
        }
        ...HpSharedItemTilesCarousel_viewer @arguments(fetchFavorites: $hasUserId, userId: $userId)
    }
`;

const carouselQuery = graphql`
    query HpSharedFeaturedModuleCarouselQuery(
        $first: Int!
        $uriRef: String!
        $hasUserId: Boolean!
        $userId: String!
        $isTrade: Boolean!
        $priceBookName: String
        $userCountryCode: String
    ) {
        viewer {
            ...HpSharedFeaturedModuleCarousel_viewer
                @arguments(
                    count: $first
                    uriRef: $uriRef
                    hasUserId: $hasUserId
                    userId: $userId
                    isTrade: $isTrade
                    priceBookName: $priceBookName
                    userCountryCode: $userCountryCode
                )
        }
    }
`;

const userFragment = graphql`
    fragment HpSharedFeaturedModuleCarousel_user on User {
        ...HpSharedItemTilesCarousel_user
    }
`;

type CarouselContentProps = {
    verticalPath: string;
    isMobile: boolean;
    userId: string;
    userRef: HpSharedFeaturedModuleCarousel_user$key | null | undefined;
};
const CarouselContent: FC<CarouselContentProps> = ({ verticalPath, isMobile, userId, userRef }) => {
    const { isTrade } = useHomePageContext();
    const hasUserId = !!userId;
    const priceBookName = getPriceBookName(document.cookie) || 'DEFAULT';
    const userCountryCode = getUserSessionCountryCode();

    const queryData = useLazyLoadQuery<HpSharedFeaturedModuleCarouselQuery>(carouselQuery, {
        first: ITEMS_PER_PAGE,
        uriRef: verticalPath,
        hasUserId,
        userId,
        isTrade,
        priceBookName,
        userCountryCode,
    });

    const { data: viewer, loadNext } = usePaginationFragment<
        HpSharedFeaturedModuleCarouselPaginationQuery,
        HpSharedFeaturedModuleCarousel_viewer$key
    >(viewerFragment, queryData.viewer);
    const user = useFragment(userFragment, userRef);

    const items = flattenConnectionNodes(viewer?.saleItems);

    const itemsToShow = isMobile ? CAROUSEL_ITEMS_TO_SHOW_MOBILE : CAROUSEL_ITEMS_TO_SHOW_RESP;

    if (!items.length || !viewer) {
        return null;
    }

    return (
        <HpSharedItemTilesCarousel
            viewer={viewer}
            user={user}
            items={items}
            isMobile={isMobile}
            carouselCapacity={MAX_ITEMS_PER_VERTICAL}
            totalResults={Math.min(viewer?.saleItems?.totalResults || 0, MAX_ITEMS_PER_VERTICAL)}
            trackingList={triggerConstants.TRIGGER_SALE_MODULE}
            loadNext={loadNext}
            fullWidth={!!isMobile}
            gapSize="small"
            itemsToShow={itemsToShow}
            showArrows={!isMobile}
            tileSize="medium"
        />
    );
};

export const HpSharedFeaturedModuleCarousel: FC<{
    user?: HpSharedFeaturedModuleCarousel_user$key | null;
    isMobile: boolean;
    verticalPath: string | null;
}> = ({ user: userRef, isMobile, verticalPath }) => {
    const isClient = useClientState();
    const userId = isClient ? getBuyerId(document.cookie) || '' : '';

    const itemsToShow = isMobile ? CAROUSEL_ITEMS_TO_SHOW_MOBILE : CAROUSEL_ITEMS_TO_SHOW_RESP;

    if (!verticalPath || !isClient) {
        return (
            <ItemTilesCarouselShimmer
                fullWidth={!!isMobile}
                gapSize="small"
                itemsToShow={itemsToShow}
                showArrows={!isMobile}
                size="medium"
            />
        );
    }

    return (
        <CarouselContent
            key={verticalPath}
            verticalPath={verticalPath}
            isMobile={isMobile}
            userId={userId}
            userRef={userRef}
        />
    );
};
